// ============================================
// BUSQUEDASSINRESULTADO.JS — lo que el equipo busca y NO encuentra
// ============================================
// BUSCADOR-PRO (2026-09-21). No es una lista de busquedas: es una lista de HUECOS DEL CATALOGO.
// Lo que sale arriba es lo que mas veces se ha buscado sin exito, que es exactamente lo que
// conviene dar de alta, pedirle a Syscom o resolver con un sinonimo.
//
// SOLO DIRECCION, con el candado en el SERVIDOR (routes/buscador.js). Dice que busca cada
// persona: es informacion sensible del equipo, igual que el registro de accesos. Se guardan
// 90 dias y el barrido diario los borra de verdad.

import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from './config/api';
import { authHeaders } from './utils/storage';
import useResponsive from './hooks/useResponsive';
// La tabla va dentro del scrollport del proyecto: acota el alto a lo que queda de ventana para
// que la barra de scroll horizontal NO acabe fuera de la pantalla. Es la regla que vigila
// scripts/portero-modales.js, nacida del bug en que no se alcanzaba el boton Guardar.
import ScrollportTabla from './components/ScrollportTabla';

const PP = "'Poppins', sans-serif";
const AZUL = '#265F97';
const GRIS = '#5e666e';
const GRIS_CLARO = '#9aa3ab';
const BORDE = '#e6e8ea';

const fecha = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? '—' : d.toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export default function BusquedasSinResultado() {
  const [datos, setDatos] = useState(null);
  const [dias, setDias] = useState(30);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [pestana, setPestana] = useState('agrupadas');
  const { isMobile } = useResponsive();

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buscador/sin-resultado?dias=${dias}`, { headers: authHeaders() })
        .then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'No se pudo leer el registro');
      setDatos(r);
    } catch (e) { setError(e.message); } finally { setCargando(false); }
  }, [dias]);

  useEffect(() => { cargar(); }, [cargar]);

  const tarjeta = {
    background: '#fff', border: `1px solid ${BORDE}`, borderRadius: 12,
    padding: isMobile ? 14 : 18, marginBottom: 14,
  };

  return (
    <div style={{ fontFamily: PP, color: GRIS, padding: isMobile ? 12 : 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#2e3338', margin: 0 }}>
          🔍 Búsquedas sin resultado
        </h2>
        <span style={{ fontSize: 12, color: GRIS_CLARO }}>solo dirección</span>
      </div>
      <p style={{ fontSize: 13, marginTop: 4, marginBottom: 16, maxWidth: 720 }}>
        Lo que el equipo escribe en La Tienda y no encuentra. Sirve para saber qué falta en el
        catálogo, qué pedirle a Syscom y qué palabras habría que enseñarle al buscador.
        Se guardan 90 días.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[7, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => setDias(d)}
            style={{
              padding: '8px 14px', minHeight: 40, borderRadius: 8, cursor: 'pointer',
              fontFamily: PP, fontSize: 13, fontWeight: d === dias ? 700 : 500,
              background: d === dias ? AZUL : '#fff', color: d === dias ? '#fff' : GRIS,
              border: `1px solid ${d === dias ? AZUL : BORDE}`,
            }}
          >{d} días</button>
        ))}
      </div>

      {cargando && <div style={{ padding: 30, textAlign: 'center', color: GRIS_CLARO }}>Cargando…</div>}
      {error && (
        <div style={{ ...tarjeta, borderColor: '#e0b4b4', background: '#fdf3f3', color: '#8a4040' }}>
          {error}
        </div>
      )}

      {!cargando && !error && datos && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {[
              ['Búsquedas fallidas', datos.resumen.total],
              ['Palabras distintas', datos.resumen.distintas],
              ['Personas', datos.resumen.personas],
            ].map(([t, v]) => (
              <div key={t} style={{ ...tarjeta, flex: '1 1 160px', marginBottom: 0, textAlign: 'center' }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: AZUL }}>{v}</div>
                <div style={{ fontSize: 11, color: GRIS_CLARO }}>{t}</div>
              </div>
            ))}
          </div>

          {datos.resumen.total === 0 ? (
            <div style={{ ...tarjeta, textAlign: 'center', color: GRIS_CLARO }}>
              Nadie se ha quedado sin encontrar nada en este periodo. Buena señal.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {[['agrupadas', 'Lo más buscado sin éxito'], ['ultimas', 'Las últimas']].map(([k, t]) => (
                  <button
                    key={k}
                    onClick={() => setPestana(k)}
                    style={{
                      padding: '8px 14px', minHeight: 40, borderRadius: 8, cursor: 'pointer',
                      fontFamily: PP, fontSize: 13, fontWeight: pestana === k ? 700 : 500,
                      background: pestana === k ? '#eef4fa' : 'transparent',
                      color: pestana === k ? AZUL : GRIS, border: 'none',
                    }}
                  >{t}</button>
                ))}
              </div>

              <ScrollportTabla estilo={{ ...tarjeta, padding: 0, marginBottom: 14 }}>
                {pestana === 'agrupadas' ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }}>
                    <thead>
                      <tr style={{ background: '#fafbfc', textAlign: 'left' }}>
                        <th style={{ padding: 10, fontSize: 11, color: GRIS_CLARO }}>QUÉ BUSCARON</th>
                        <th style={{ padding: 10, fontSize: 11, color: GRIS_CLARO }}>VECES</th>
                        <th style={{ padding: 10, fontSize: 11, color: GRIS_CLARO }}>PERSONAS</th>
                        <th style={{ padding: 10, fontSize: 11, color: GRIS_CLARO }}>SE LE OFRECIÓ</th>
                        <th style={{ padding: 10, fontSize: 11, color: GRIS_CLARO }}>ÚLTIMA VEZ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.agrupadas.map((a, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${BORDE}` }}>
                          <td style={{ padding: 10, fontWeight: 600, color: '#2e3338' }}>{a.ejemplo}</td>
                          <td style={{ padding: 10, fontWeight: 700, color: a.veces > 2 ? '#b4522a' : GRIS }}>{a.veces}</td>
                          <td style={{ padding: 10 }}>{a.personas}</td>
                          <td style={{ padding: 10, color: a.sugerencia ? GRIS : GRIS_CLARO }}>{a.sugerencia || '—'}</td>
                          <td style={{ padding: 10, color: GRIS_CLARO }}>{fecha(a.ultima)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }}>
                    <thead>
                      <tr style={{ background: '#fafbfc', textAlign: 'left' }}>
                        <th style={{ padding: 10, fontSize: 11, color: GRIS_CLARO }}>CUÁNDO</th>
                        <th style={{ padding: 10, fontSize: 11, color: GRIS_CLARO }}>QUÉ ESCRIBIÓ</th>
                        <th style={{ padding: 10, fontSize: 11, color: GRIS_CLARO }}>QUIÉN</th>
                        <th style={{ padding: 10, fontSize: 11, color: GRIS_CLARO }}>CON FILTROS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.ultimas.map((u, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${BORDE}` }}>
                          <td style={{ padding: 10, color: GRIS_CLARO, whiteSpace: 'nowrap' }}>{fecha(u.fecha)}</td>
                          <td style={{ padding: 10, fontWeight: 600, color: '#2e3338' }}>{u.texto}</td>
                          <td style={{ padding: 10 }}>{u.usuario_nombre || '—'}</td>
                          <td style={{ padding: 10, color: GRIS_CLARO }}>{u.con_filtros ? 'sí' : 'no'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </ScrollportTabla>
            </>
          )}
        </>
      )}
    </div>
  );
}
